/**
 * Presentation helpers for the cross-course seat projection (issue #465, 1a).
 *
 * The server sends `waiting` as PARTS, not prose: which facts hold, with their
 * numbers and instants. Composing the sentence here is the boundary that lets
 * copy change without a server release, and it is the same split the session
 * portal already uses. Every function below is a lookup or a join — none of them
 * ranks, filters, hides, or combines anything the server ordered — with one
 * exception: `firstWaitingPart` picks the edition's one interruption by the
 * key the server already ranks the attention register with.
 *
 * Two rules this file exists to hold:
 *
 *   1. Every instant is formatted in the COURSE's zone. A seat spans courses in
 *      different zones, so there is no single frame zone to stamp once, and a
 *      learner one zone east must not read a different due date from the one
 *      their instructor set. With no zone there is no instant: the caller gets
 *      `null` and states the fact without the time rather than reaching for the
 *      browser's.
 *   2. `empty` and `unavailable` never sound alike. A read that did not complete
 *      is not a zero, and none of the sentences below let it read as one.
 */
import type {
  LearningPrimaryAction,
  LearningWaitingPart,
  SeatAttentionItem,
  SeatContinuationItem,
  TeachingPrimaryAction,
  TeachingWaitingPart,
} from "@/lib/api/seatWorkspace";

/** `Oct 7, 2026, 6:30 PM EDT` — the instant, in the course's zone, saying which. */
export function formatCourseInstant(value: string, timezone: string | null, withWeekday = false): string | null {
  if (!timezone) return null;
  return new Intl.DateTimeFormat(undefined, {
    ...(withWeekday ? { weekday: "short" as const } : {}),
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
    timeZoneName: "short",
  }).format(new Date(value));
}

/**
 * The session-level roll fact, in ADR 0035's words, as All sessions says it. Home
 * says it once per session, beside the act that takes the roll: on the attention
 * register's "Take the roll" row, or on the week register's row for a session
 * the attention register does not list. A learner's "Not recorded" on a roll
 * row is a different fact.
 */
export const ROLL_NOT_RECORDED = "Roll not recorded";

/**
 * Describe the server's selection without making a second ranking decision.
 * The strip names what to do now; a fact a register below says (the roll, a
 * session's time) is not said again here.
 */
export const TEACHING_ACTION_HEADING: Record<TeachingPrimaryAction["reason"], string> = {
  class_in_progress: "Your session is scheduled now",
  // The server's act beside it is "Take the roll"; the attention row says the roll is not recorded.
  register_open: "Your session today",
  // Coursework's words: an attempt, to review.
  oldest_review: "Oldest attempt to review",
  next_class: "Prepare for your next session",
  next_deadline: "Check the next assignment deadline",
};

/** The learner's next-step strip heading, one per server reason. */
export const LEARNING_ACTION_HEADING: Record<LearningPrimaryAction["reason"], string> = {
  class_in_progress: "Your session is scheduled now",
  returned_revision: "Revise returned work",
  due_unattempted: "Start your next assignment",
  next_class: "Prepare for your next session",
  evidence: "Review your latest work",
};

/** The same instant to the day, still in the course's zone. */
export function formatCourseDay(value: string, timezone: string | null): string | null {
  if (!timezone) return null;
  return new Intl.DateTimeFormat(undefined, {
    dateStyle: "medium",
    timeZone: timezone,
  }).format(new Date(value));
}

/**
 * Teaching Home's words for what is waiting: one wording per fact, and these
 * two tables are the only place it is written. The attention register's notice
 * speaks about its one item ("Overdue"); the course edition's interruption
 * speaks about the course ("Overdue work"). A fact added to one table gets its
 * words in the other in the same change.
 *
 * Both are total by construction: a `kind` the server starts sending without
 * words here is a compile error, not a silently dropped fact. The register
 * never prints the server's `condition` sentence, visibly or as a description,
 * and never reads it: an absence row's number arrives as its typed `count`.
 * No edition clause prints a part's `title`: for overdue, returned, and absence
 * rows the server's label names a learner, and learner names do not belong on
 * a course collection. Only `review_backlog` carries a count the server proved,
 * so only it prints a number there; the others name WHAT is waiting, never how
 * much.
 */
const TEACHING_ATTENTION_NOTICE: Record<SeatAttentionItem["kind"], (item: SeatAttentionItem) => string> = {
  review: () => "To review",
  attendance: () => ROLL_NOT_RECORDED,
  overdue: () => "Overdue",
  returned: () => "Revision awaited",
  absences: (item) => item.count === null ? "Absences to follow up" : `${item.count} absences to follow up`,
};

const TEACHING_ATTENTION_CLAUSE: Record<
  TeachingWaitingPart["kind"],
  (part: TeachingWaitingPart, timezone: string | null) => string
> = {
  // "n to review", the Coursework register's words for the same work.
  review_backlog: (part) => part.count === null ? "Work to review" : `${part.count} to review`,
  // The fact is "today's scheduled session has no completed roll", which is true
  // all day; it claims nothing about whether the session happened.
  register_open: () => ROLL_NOT_RECORDED,
  next_deadline: (part, timezone) => {
    const due = part.at ? formatCourseDay(part.at, timezone) : null;
    return due ? `Deadline ${due}` : "Deadline ahead";
  },
  overdue: () => "Overdue work",
  returned: () => "Revisions awaited",
  absences: () => "Absences to follow up",
};

/**
 * Each waiting part's row kind in the attention register. `next_deadline` has
 * no row there: it is a date ahead, not work waiting.
 */
const ATTENTION_REGISTER_KIND: Record<TeachingWaitingPart["kind"], SeatAttentionItem["kind"] | null> = {
  review_backlog: "review",
  register_open: "attendance",
  next_deadline: null,
  overdue: "overdue",
  returned: "returned",
  absences: "absences",
};

/** `[has a register row, instant, register kind]`, the server's sort key. */
function attentionRank(part: TeachingWaitingPart): [number, number, string] {
  const kind = ATTENTION_REGISTER_KIND[part.kind];
  const at = part.at === null ? Number.NaN : Date.parse(part.at);
  return kind === null || Number.isNaN(at) ? [1, 0, part.kind] : [0, at, kind];
}

/**
 * The course's first waiting part in the order the attention register ranks
 * its rows, or null when nothing is waiting.
 *
 * The server sends a course's parts in a fixed order (reviews, the open roll,
 * then overdue, returned and absences) so they never reorder between reads.
 * The attention register ranks the same facts oldest first, then by kind
 * (`_attention_sort_key` in `seat_projection.py`), and each part's `at` is its
 * kind's oldest row there. Sorting the parts on that key therefore puts the
 * course's first register row first, so the edition leads with the fact the
 * register lists first for that course. A part with no register row or no
 * instant ranks after every part that has both; ties keep the server's order.
 */
export function firstWaitingPart(parts: TeachingWaitingPart[]): TeachingWaitingPart | null {
  return [...parts].sort((a, b) => {
    const [rowA, atA, kindA] = attentionRank(a);
    const [rowB, atB, kindB] = attentionRank(b);
    return rowA - rowB || atA - atB || (kindA < kindB ? -1 : kindA > kindB ? 1 : 0);
  })[0] ?? null;
}

/** An attention register row's one notice. */
export function teachingAttentionNotice(item: SeatAttentionItem): string {
  return TEACHING_ATTENTION_NOTICE[item.kind](item);
}

/** The edition's one interruption: its course's first waiting part, or none. */
export function teachingInterruption(
  parts: TeachingWaitingPart[],
  timezone: string | null,
): string | null {
  const part = firstWaitingPart(parts);
  return part ? TEACHING_ATTENTION_CLAUSE[part.kind](part, timezone) : null;
}

/** `2026-09-30` for an instant, in the given zone. */
function zonedDay(value: Date, timeZone: string): string {
  return new Intl.DateTimeFormat("en-CA", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone,
  }).format(value);
}

function nextDay(day: string): string {
  const [year, month, date] = day.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, date + 1)).toISOString().slice(0, 10);
}

/**
 * `Today, 6:00 PM`, `Tomorrow, 5:30 PM`, or `Thu, Oct 1, 5:30 PM`.
 *
 * Both days are read in the COURSE's zone, and "today" is the server's
 * `generated_at`, never the browser clock, so the word agrees with the read
 * that produced the session. `withZone` names the zone when a collection spans
 * more than one; a single-zone collection states its zone once instead.
 */
export function formatClassWhen(
  startsAt: string,
  timezone: string,
  reference: string,
  withZone = false,
): string {
  const start = new Date(startsAt);
  const time = new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
    timeZone: timezone,
    ...(withZone ? { timeZoneName: "short" as const } : {}),
  }).format(start);
  const startDay = zonedDay(start, timezone);
  const today = zonedDay(new Date(reference), timezone);
  if (startDay === today) return `Today, ${time}`;
  if (startDay === nextDay(today)) return `Tomorrow, ${time}`;
  const date = new Intl.DateTimeFormat(undefined, {
    weekday: "short",
    month: "short",
    day: "numeric",
    ...(startDay.slice(0, 4) === today.slice(0, 4) ? {} : { year: "numeric" as const }),
    timeZone: timezone,
  }).format(start);
  return `${date}, ${time}`;
}

/**
 * Whether the times in one collection name their zone: only when the
 * collection spans several. One zone is said by none of its rows. Home's
 * editions and week register, and All sessions, read this one rule.
 */
export function namesZone(zones: Iterable<string>): boolean {
  return new Set(zones).size > 1;
}

/** `America/New_York` -> `New York`. A zone with no city reads as itself. */
export function zoneCity(timezone: string): string {
  return (timezone.split("/").pop() ?? timezone).replace(/_/g, " ");
}

/**
 * The learner's course object line. Titles here are the learner's own
 * assignments, so they are named. `due` and `returned` ask the learner to act;
 * a receipt and released feedback are news, and the object marks them as such.
 */
const LEARNING_ATTENTION_CLAUSE: Record<
  LearningWaitingPart["kind"],
  (part: LearningWaitingPart, timezone: string | null) => string
> = {
  due: (part, timezone) => {
    const due = part.at ? formatShortDay(part.at, timezone) : null;
    const head = part.title ?? "Coursework";
    return due ? `${head} due ${due}` : `${head} due`;
  },
  returned: (part) => `${part.title ?? "Your work"} returned for revision`,
  // "attempt 1" ships; the denominator does not, because no `max_attempts`
  // column exists anywhere to be honest about.
  receipt: (part) => `Attempt ${part.count ?? 1} recorded`,
  feedback_released: (part) => part.count === null
    ? "Results released"
    : `${part.count} ${part.count === 1 ? "result" : "results"} released`,
};

const LEARNING_ACTION_KINDS: ReadonlySet<LearningWaitingPart["kind"]> = new Set(["due", "returned"]);

export function learningAttentionLine(
  parts: LearningWaitingPart[],
  timezone: string | null,
): { text: string; tone: "act" | "news" } {
  return {
    text: parts.map((part) => LEARNING_ATTENTION_CLAUSE[part.kind](part, timezone)).join(" · "),
    tone: parts.some((part) => LEARNING_ACTION_KINDS.has(part.kind)) ? "act" : "news",
  };
}

/** `Oct 15`, in the course's zone; no zone, no date. */
function formatShortDay(value: string, timezone: string | null): string | null {
  if (!timezone) return null;
  return new Intl.DateTimeFormat(undefined, { month: "short", day: "numeric", timeZone: timezone })
    .format(new Date(value));
}

/**
 * The one thing a learner is invited to DO, on work that is still open.
 *
 * Kept verbatim from `course_action_ranker._ATTEMPT_LABEL`, which is the only
 * other place this invitation is spoken.
 */
const ATTEMPT_LABEL = "Open assignment";

/**
 * What the control offers on a CLOSED row: a record that already exists, to
 * read.
 *
 * A separate vocabulary rather than a fallback through the label above, for the
 * reason the server ranker already writes out — a closed row's own detail
 * sentence says no further attempt can be made, so offering "Open assignment"
 * beneath it contradicts the sentence with the control. The row itself is kept
 * deliberately: a closed record stays reachable for its receipt and feedback
 * evidence, so only the label changes and never the href.
 *
 * `submitted`, `resubmitted`, `returned`, and `graded` are the server's own
 * mapping (`course_action_ranker._EVIDENCE_LABEL`). `unattempted` is not in it,
 * because the server's evidence rung is gated on a receipt existing and this
 * row is not: a closed assignment with no attempt still lists here, and the
 * only truthful thing its control opens is the assignment record.
 */
const EVIDENCE_LABEL: Record<SeatContinuationItem["learner_state"], string> = {
  unattempted: "View assignment",
  submitted: "View receipt",
  resubmitted: "View receipt",
  returned: "View feedback",
  graded: "View feedback",
};

/**
 * The structural slice shared by the per-course and cross-course projections.
 * Both carry the same server-owned assignment status and learner state, while
 * their surrounding records differ.
 */
type ContinuationActionItem = Pick<SeatContinuationItem, "assignment" | "learner_state">;

/** Which of the two disjoint vocabularies one continuation row speaks. */
export function continuationActionLabel(item: ContinuationActionItem): string {
  return item.assignment.status === "closed"
    ? EVIDENCE_LABEL[item.learner_state]
    : ATTEMPT_LABEL;
}

/** Read the server's box offer, withdrawing the fill if any ranking fact is invalid. */
export function homeOffer(projection: Pick<import("./api/seatWorkspace").SeatProjection, "ranking" | "offers">, box: string, rows: ReadonlySet<string>, stale = false) {
  const none = { offers: undefined, candidate: { eligible: false, blocked: null, rank: null } } as const;
  const ranking = projection.ranking;
  if (stale || ranking?.state !== "ready") return none;
  const values = ranking.value.candidates;
  if (!Array.isArray(values) || new Set(values.map(item => item.id)).size !== values.length || values.some(item =>
    typeof item.eligible !== "boolean" || typeof item.blocked !== "boolean" || !["ready", "empty", "unavailable"].includes(item.read)
    || (item.rank !== null && (!Number.isInteger(item.rank) || item.rank < 0)))) return none;
  const ranked = values.filter(item => item.rank !== null).map(item => item.rank);
  if (new Set(ranked).size !== ranked.length) return none;
  const value = values.find(item => item.id === box);
  const key = projection.offers?.[box];
  if (!value || value.read !== "ready" || value.blocked || !value.eligible || !key || !rows.has(key)) return none;
  return { offers: key, candidate: { eligible: true, blocked: null, rank: value.rank } };
}
